/**
 * CoachFAB Component
 *
 * Floating action button displayed on all operator tabs providing quick
 * access to the AI Coach. Positioned above the tab bar (fixed bottom-24 right-4).
 *
 * Behavior:
 * - 48px guild-green circle with MessageCircle icon
 * - Subtle pulse animation on first appearance (once, tracked in localStorage)
 * - Hidden when coach is disabled (via coachEnabled from dashboard data)
 * - Navigates to /operator/coach on tap
 */

import { useNavigate } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { useState } from 'react';
import { cn } from '../../../lib/utils';

const PULSE_SEEN_KEY = 'op_coachFabPulseSeen';

interface CoachFABProps {
  /** Whether the coach feature is enabled for this org. When false, FAB is hidden. */
  coachEnabled?: boolean;
}

export function CoachFAB({ coachEnabled }: CoachFABProps) {
  const navigate = useNavigate();

  // Track whether pulse animation has been shown before
  const [shouldPulse] = useState(() => {
    try {
      if (localStorage.getItem(PULSE_SEEN_KEY) === 'true') return false;
      localStorage.setItem(PULSE_SEEN_KEY, 'true');
      return true;
    } catch {
      return false;
    }
  });

  // Hide if coach is disabled
  if (coachEnabled === false) return null;

  return (
    <>
      {/* Pulse animation keyframe (only rendered once, respects reduced motion) */}
      {shouldPulse && (
        <style>{`
          @media (prefers-reduced-motion: no-preference) {
            @keyframes coachFabPulse {
              0% { box-shadow: 0 0 0 0 rgba(0, 103, 74, 0.4); }
              70% { box-shadow: 0 0 0 12px rgba(0, 103, 74, 0); }
              100% { box-shadow: 0 0 0 0 rgba(0, 103, 74, 0); }
            }
          }
        `}</style>
      )}

      <button
        type="button"
        onClick={() => navigate('/operator/coach')}
        aria-label="Open AI Coach"
        className={cn(
          'fixed bottom-24 right-4 z-[45]',
          'flex h-12 w-12 items-center justify-center rounded-full',
          'bg-guild-green text-white shadow-lg',
          'transition-transform duration-150 active:scale-95',
          'focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2',
        )}
        style={shouldPulse ? { animation: 'coachFabPulse 2s ease-out 1' } : undefined}
      >
        <MessageCircle size={22} strokeWidth={1.5} />
      </button>
    </>
  );
}
